/**
 * QC Return for Correction Modal (Phase 1.5 UX — Feb 2026)
 *
 * Layout goals (per stakeholder UX feedback):
 *   • Compact header with order context inline (Order #, Product, Submitter)
 *   • Replace the bulky yellow info panel with a single status pill
 *   • Two-column top row (Error Category × Severity) saves vertical space
 *   • Quick-issue templates pre-populate Correction Notes
 *   • Sticky footer keeps Cancel + Return for Correction always visible
 *   • Scrollable body bounded by 90vh so footer never gets pushed off
 *
 * Backend contract (POST /api/orders/{id}/qc-return):
 *   rejection_category : QCRejectionCategory  (required)
 *   error_type         : str | null           (optional)
 *   rejection_reason   : str                  (required, 1-2000)
 *   evidence_file_ids  : list[str]            (optional, max 5)
 *   severity           : "minor"|"major"|"critical" | null  (optional, new)
 *
 * Internal DB status remains `pending_correction`. User-facing label is
 * always "Correction Required" everywhere in the app.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  X, ChevronDown, Paperclip, UploadCloud, ShieldAlert,
  FileCheck2, Layers, Tag, Zap, PenLine, SendHorizonal,
} from 'lucide-react';
import { toast } from 'sonner';
import './qc-return-modal.css';
import { isTaxProduct } from '../../utils/orderHelpers';
import { qcComponentOf } from '../../lib/qcComponent';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ERROR_CATEGORIES = [
  { value: 'data_error',          label: 'Data Error' },
  { value: 'missing_info',        label: 'Missing Info' },
  { value: 'document_issue',      label: 'Document Issue' },
  { value: 'calculation_issue',   label: 'Calculation Issue' },
  { value: 'wrong_jurisdiction',  label: 'Wrong Jurisdiction' },
];

const SEVERITIES = [
  { value: 'minor',    label: 'Minor' },
  { value: 'major',    label: 'Major' },
  { value: 'critical', label: 'Critical' },
];

const QUICK_TEMPLATES = [
  { label: 'Wrong Parcel',                   notes: 'The parcel number(s) on the certificate do not match the property of record. Please verify against the source and update before resubmitting.' },
  { label: 'Missing Tax Year',               notes: 'One or more required tax years are missing from the certificate. Please add the missing year(s) and confirm balances.' },
  { label: 'Incorrect Collector',            notes: 'The tax collector / payee name or address is incorrect. Please confirm against the official source and update accordingly.' },
  { label: 'Incorrect Payoff',               notes: 'The payoff amount(s) do not match the official statement. Please recalculate and correct each affected installment.' },
  { label: 'Missing Attachment',             notes: 'A required supporting document is missing. Please attach the missing file before resubmitting.' },
  { label: 'Narrative Error',                notes: 'The narrative contains incorrect or unclear information. Please revise so it accurately reflects the certificate findings.' },
  { label: 'Incorrect Tax Amounts',          notes: 'One or more tax amounts are incorrect. Please verify each installment against the official source and correct the figures.' },
  { label: 'Prior Taxes Not Confirmed',      notes: 'Prior-year tax status has not been confirmed. Please verify that prior taxes are paid (or call out any delinquencies) before resubmitting.' },
  { label: 'Local Entity Taxes Not Confirmed', notes: 'Local entity / municipal taxes have not been confirmed. Please verify all applicable local entities and document their status.' },
];

const QCReturnModal = ({ isOpen, onClose, onConfirm, order }) => {
  const [category, setCategory] = useState('');
  const [errorType, setErrorType] = useState('');
  const [severity, setSeverity] = useState(''); // mandatory — no preselection
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);
  // MLS-DUAL-001 Phase 3: component-granular return for MLS dual orders
  const [affectedComponents, setAffectedComponents] = useState([]);
  // MLS-COMPONENT-QC-001: a component-level item is fixed to that component — no picker
  const isMlsDual = !qcComponentOf(order) && (['MLS', 'MLS Update', 'Update MLS'].includes(order?.client_product)
    || !!order?.component_rollup || !!(order?.components && order.components.mls));
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  // QC-RETURN-UI-001: presentation-only state (closing transition, drag-over highlight)
  const [closing, setClosing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const closeTimer = useRef(null);
  const requestClose = () => {
    if (closing) return;
    setClosing(true);
    closeTimer.current = setTimeout(() => { setClosing(false); onClose(); }, 160);
  };

  // QC-RETURN-SIMPLIFY-001: Title Search QC gets a simplified form —
  // no Error Type / Quick Templates, evidence limited to 1 file / 2 MB.
  const isTitleQC = !isTaxProduct(order);
  const MAX_FILES = isTitleQC ? 1 : 5;
  const MAX_SIZE = (isTitleQC ? 2 : 10) * 1024 * 1024;
  const MAX_SIZE_LABEL = isTitleQC ? '2 MB' : '10 MB';

  useEffect(() => {
    const close = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !isSubmitting) requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isSubmitting]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      setClosing(false); setDragging(false);
      setCategory(''); setErrorType(''); setSeverity('');
      // TYPING-RETURN-TO-QC-001 — pre-quote the Typing return so the Search Processor gets full context
      const typingReason = order?.typing_status === 'returned_to_qc' ? (order?.typing_return_reason || '').trim() : '';
      setNotes(typingReason ? `Typing return reason (${order?.typing_returned_by_name || 'Typing'}): "${typingReason}"\n\nQC notes: ` : '');
      setError(''); setEvidence([]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !order) return null;

  /* ── Evidence upload ───────────────────────────────────────── */
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (evidence.length + files.length > MAX_FILES) {
      toast.error(MAX_FILES === 1
        ? 'Only 1 evidence file is allowed per return.'
        : `Maximum ${MAX_FILES} evidence files per return.`);
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      for (const file of files) {
        if (file.size > MAX_SIZE) { toast.error(`${file.name} exceeds the ${MAX_SIZE_LABEL} limit.`); continue; }
        const fd = new FormData();
        fd.append('file', file);
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const mime = (file.type || '').toLowerCase();
        let backendCategory = 'supporting_document';
        if (mime.startsWith('image/') || ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'pdf' || mime === 'application/pdf') {
          backendCategory = 'image';
        } else if (ext === 'txt' || mime === 'text/plain') {
          backendCategory = 'internal_note';
        }
        fd.append('category', backendCategory);
        const res = await fetch(`${API}/orders/${order.id}/files/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (res.ok) {
          const body = await res.json();
          const fid = body.file_id || body.id || body.fileId;
          if (fid) {
            setEvidence(prev => [...prev, { file_id: fid, filename: file.name, size: file.size }]);
          } else {
            toast.error(`Upload succeeded but no file_id returned for ${file.name}`);
          }
        } else {
          const d = await res.json().catch(() => ({}));
          toast.error(`Upload failed: ${d.detail || file.name}`);
        }
      }
    } catch {
      toast.error('Network error during upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeEvidence = (fid) => setEvidence(prev => prev.filter(e => e.file_id !== fid));

  const applyTemplate = (tpl) => {
    setNotes(tpl.notes);
    setErrorType((prev) => prev || tpl.label);
    if (error) setError('');
  };

  const selectedLabel = ERROR_CATEGORIES.find(c => c.value === category)?.label || 'Select error category';
  const isValid = category && severity && notes.trim().length > 0 && (!isMlsDual || affectedComponents.length > 0);

  const handleConfirm = async () => {
    if (!category) { setError('Please select an error category.'); return; }
    if (!severity) {
      // QC-CONSOLIDATE-001 (Feb 2026): mirrors the backend 400 message.
      setError('Severity is required — choose Minor, Major, or Critical.');
      return;
    }
    if (!notes.trim()) { setError('Please describe what the processor must fix.'); return; }
    if (isMlsDual && affectedComponents.length === 0) {
      setError('Select the affected component(s) — Tax Search, MLS Search, or both.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      // QCInlineActions / QCDashboard onConfirm signature (kept stable):
      //   (category, errorType, notes, evidenceFileIds, severity, affectedComponents)
      await onConfirm(category, errorType.trim() || null, notes.trim(), evidence.map(e => e.file_id), severity,
        isMlsDual ? affectedComponents : null);
      setCategory(''); setErrorType(''); setSeverity(''); setNotes(''); setEvidence([]); setAffectedComponents([]);
    } catch (err) {
      setError(err.message || 'Failed to return order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancel = () => { if (!isSubmitting) requestClose(); };

  return (
    <div className={`qcrm-root ${closing ? 'is-closing' : ''}`} role="dialog" aria-modal="true" aria-labelledby="qcrm-title">
      <div className="qcrm-overlay" onClick={cancel} />

      <div className="qcrm-shell" data-testid="qc-return-modal">
        {/* ── Gradient header ─────────────────────────────────────── */}
        <div className="qcrm-header">
          <div className="qcrm-header-icon" aria-hidden="true"><FileCheck2 className="w-6 h-6" strokeWidth={1.9} /></div>
          <div className="min-w-0 flex-1">
            <h2 id="qcrm-title" className="text-[22px] font-bold leading-tight tracking-tight">Return for Correction</h2>
            <p className="qcrm-meta">
              <span className="font-semibold">{order.order_number}</span>
              {order.client_product && <><span className="qcrm-dot" />{order.client_product}</>}
              {order.qc_submitted_by_name && <><span className="qcrm-dot" />By {order.qc_submitted_by_name}</>}
            </p>
          </div>
          <button type="button" onClick={cancel} className="qcrm-close" aria-label="Close" data-testid="qc-return-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────── */}
        <div className="qcrm-body">
          {isMlsDual && (
            <div className="qcrm-section" data-testid="qc-return-affected-components">
              <label className="qcrm-label"><Layers className="qcrm-label-icon" /> Affected Component(s) <span className="qcrm-req">*</span></label>
              <div className="qcrm-components">
                {[['tax', 'Tax Search'], ['mls', 'MLS Search']].map(([val, label]) => (
                  <label key={val} className={`qcrm-check ${affectedComponents.includes(val) ? 'is-on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={affectedComponents.includes(val)}
                      onChange={(e) => setAffectedComponents((prev) =>
                        e.target.checked ? [...prev, val] : prev.filter((c) => c !== val))}
                      data-testid={`qc-return-component-${val}`}
                    />
                    {label}
                  </label>
                ))}
                <span className="qcrm-hint ml-auto">Only selected component(s) reopen — the other stays complete</span>
              </div>
            </div>
          )}

          {/* Row: Error Category × Severity */}
          <div className="qcrm-row">
            <div className="qcrm-section" ref={dropdownRef}>
              <label className="qcrm-label"><Layers className="qcrm-label-icon" /> Error Category <span className="qcrm-req">*</span></label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  className={`qcrm-select ${!category && error ? 'is-invalid' : ''} ${showDropdown ? 'is-open' : ''}`}
                  aria-haspopup="listbox" aria-expanded={showDropdown}
                  data-testid="qc-return-category"
                >
                  <span className={category ? 'text-slate-900' : 'text-slate-400'}>{selectedLabel}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showDropdown && (
                  <div className="qcrm-options" role="listbox">
                    {ERROR_CATEGORIES.map(c => (
                      <button
                        key={c.value} type="button" role="option" aria-selected={category === c.value}
                        onClick={() => { setCategory(c.value); setShowDropdown(false); if (error) setError(''); }}
                        className={`qcrm-option ${category === c.value ? 'is-selected' : ''}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="qcrm-section">
              <label className="qcrm-label"><ShieldAlert className="qcrm-label-icon" /> Severity <span className="qcrm-req">*</span></label>
              <div className={`qcrm-severity ${!severity && error ? 'is-invalid' : ''}`} role="radiogroup" data-testid="qc-return-severity">
                {SEVERITIES.map(s => {
                  const active = severity === s.value;
                  return (
                    <button
                      key={s.value} type="button" role="radio" aria-checked={active}
                      onClick={() => { setSeverity(s.value); if (error) setError(''); }}
                      className={`qcrm-sev qcrm-sev-${s.value} ${active ? 'is-active' : ''}`}
                      data-testid={`qc-return-severity-${s.value}`}
                    >
                      <span className="qcrm-radio" aria-hidden="true" />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {!isTitleQC && (
            <div className="qcrm-section">
              <label className="qcrm-label"><Tag className="qcrm-label-icon" /> Error Type <span className="qcrm-opt">(Optional)</span></label>
              <input
                type="text" value={errorType} onChange={e => setErrorType(e.target.value)}
                placeholder="e.g. Missing collector phone, typo in mailing address..."
                className="qcrm-input"
                maxLength={100} data-testid="qc-return-error-type"
              />
            </div>
          )}

          {!isTitleQC && (
            <div className="qcrm-section" data-testid="qc-return-templates">
              <label className="qcrm-label"><Zap className="qcrm-label-icon" /> Quick Templates <span className="qcrm-opt">(Optional — pre-fill notes)</span></label>
              <div className="qcrm-chips">
                {QUICK_TEMPLATES.map(tpl => {
                  const selected = notes === tpl.notes;
                  return (
                    <button
                      key={tpl.label} type="button" aria-pressed={selected}
                      onClick={() => applyTemplate(tpl)}
                      className={`qcrm-chip ${selected ? 'is-selected' : ''}`}
                      data-testid={`qc-return-template-${tpl.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {tpl.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="qcrm-section">
            <label className="qcrm-label"><PenLine className="qcrm-label-icon" /> Correction Notes <span className="qcrm-req">*</span> <span className="qcrm-opt">(What the processor must fix)</span></label>
            <textarea
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Describe specifically what needs to be corrected..."
              className={`qcrm-input qcrm-textarea ${!notes.trim() && error ? 'is-invalid' : ''}`}
              rows={3} maxLength={1000}
              data-testid="qc-return-notes"
            />
            <div className="flex justify-between items-start mt-1">
              {error ? <p className="qcrm-error" role="alert" data-testid="qc-return-error">{error}</p> : <span />}
              <span className="qcrm-hint tabular-nums">{notes.length} / 1000</span>
            </div>
          </div>

          <div className="qcrm-section">
            <label className="qcrm-label"><Paperclip className="qcrm-label-icon" /> Evidence <span className="qcrm-opt">
              (Optional, max {MAX_FILES} {MAX_FILES === 1 ? 'file' : 'files'} / {MAX_SIZE_LABEL}{MAX_FILES === 1 ? '' : ' each'})
            </span></label>
            <input
              ref={fileInputRef} type="file" multiple={MAX_FILES > 1} accept=".png,.jpg,.jpeg,.pdf,.txt,.docx"
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden" data-testid="qc-return-evidence-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (!uploading && evidence.length < MAX_FILES) setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); if (!uploading && evidence.length < MAX_FILES) handleFiles(e.dataTransfer.files); }}
              disabled={uploading || evidence.length >= MAX_FILES}
              className={`qcrm-drop ${dragging ? 'is-dragging' : ''}`}
              data-testid="qc-return-evidence-add"
            >
              <UploadCloud className={`w-6 h-6 ${uploading ? 'animate-pulse' : ''}`} />
              <span className="text-sm text-slate-800">
                {uploading
                  ? 'Uploading…'
                  : evidence.length === 0
                    ? <>Drag &amp; drop files here or <span className="qcrm-link">click to upload</span></>
                    : MAX_FILES === 1
                      ? 'Evidence file attached (1/1)'
                      : <>Add more (<span className="tabular-nums">{evidence.length}/{MAX_FILES}</span>)</>}
              </span>
              {evidence.length === 0 && !uploading && (
                <span className="text-xs text-slate-500">Add evidence {MAX_FILES === 1 ? 'file' : 'files'} (screenshots, marked-up PDFs)</span>
              )}
            </button>
            {evidence.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {evidence.map(ev => (
                  <div key={ev.file_id} className="qcrm-file" data-testid={`qc-return-evidence-${ev.file_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                      <span className="truncate text-slate-800">{ev.filename}</span>
                      <span className="text-[11px] text-slate-400 flex-shrink-0">{(ev.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <button type="button" onClick={() => removeEvidence(ev.file_id)} className="qcrm-file-remove" aria-label="Remove evidence">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────── */}
        <div className="qcrm-footer">
          <button type="button" onClick={cancel} disabled={isSubmitting} className="qcrm-btn qcrm-btn-secondary" data-testid="qc-return-cancel">
            Cancel
          </button>
          <button
            type="button" onClick={handleConfirm}
            disabled={isSubmitting || !isValid}
            className="qcrm-btn qcrm-btn-primary"
            data-testid="qc-return-confirm"
          >
            {isSubmitting ? (
              <><span className="qcrm-spinner" aria-hidden="true" /> Returning…</>
            ) : (
              <><SendHorizonal className="w-4 h-4" /> Return for Correction</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QCReturnModal;
